/**
 * LeagueContext — single source of truth for the currently active league.
 *
 * Why:
 *   Before this provider, every page (SwitchProfile, SetupScreen,
 *   PersonalDashboard) independently called getActiveLeagueId() and then
 *   re-fetched the league via api.getLeagueById(). That left a window where
 *   two components could disagree about which league is "active" (e.g., right
 *   after a `setActiveLeagueId(...)` call from /welcome but before the next
 *   page re-mounted). Centralising the state guarantees every subscriber
 *   reads the same {id, league} pair.
 *
 * Persistence:
 *   The localStorage helpers in `@/lib/league` remain the storage layer; this
 *   provider just wraps them and broadcasts changes via React state. Pages can
 *   keep using `getActiveLeagueId()` directly if they want raw access, but the
 *   recommended path is `useActiveLeague()`.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  getActiveLeagueId,
  setActiveLeagueId as persistActiveLeagueId,
  clearActiveLeague as persistClearActiveLeague,
} from "@/lib/league";
import { api } from "@/lib/api";

const LeagueContext = createContext(null);

export function LeagueProvider({ children }) {
  const [activeLeagueId, _setId] = useState(() => getActiveLeagueId());
  const [activeLeague, setActiveLeague] = useState(null);
  const [loading, setLoading] = useState(false);

  // Whenever the id changes (initial mount or explicit set), fetch the league.
  useEffect(() => {
    let alive = true;
    if (!activeLeagueId) { setActiveLeague(null); return; }
    setLoading(true);
    api.getLeagueById(activeLeagueId)
      .then((l) => { if (alive) setActiveLeague(l || null); })
      .catch(() => { if (alive) setActiveLeague(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [activeLeagueId]);

  // Cross-tab sync: if another tab updates localStorage, mirror it here.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "stakes_active_league_id") _setId(e.newValue || null);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setActiveLeagueIdAndPersist = useCallback((id) => {
    persistActiveLeagueId(id);
    _setId(id || null);
  }, []);

  const clearActiveLeague = useCallback(() => {
    persistClearActiveLeague();
    _setId(null);
    setActiveLeague(null);
  }, []);

  const refreshActiveLeague = useCallback(async () => {
    if (!activeLeagueId) return null;
    const l = await api.getLeagueById(activeLeagueId).catch(() => null);
    setActiveLeague(l || null);
    return l;
  }, [activeLeagueId]);

  const value = useMemo(() => ({
    activeLeagueId,
    activeLeague,
    loading,
    setActiveLeagueId: setActiveLeagueIdAndPersist,
    clearActiveLeague,
    refreshActiveLeague,
  }), [activeLeagueId, activeLeague, loading, setActiveLeagueIdAndPersist, clearActiveLeague, refreshActiveLeague]);

  return <LeagueContext.Provider value={value}>{children}</LeagueContext.Provider>;
}

export function useActiveLeague() {
  const ctx = useContext(LeagueContext);
  if (!ctx) throw new Error("useActiveLeague must be used inside <LeagueProvider>");
  return ctx;
}
